import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useTechnologies } from '../../hooks/useAPI';
import { technologiesAPI } from '../../services/api';
import { ImageInput } from '../components/ImagePicker';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const TechnologiesPage = () => {
  const { data: technologies, isLoading, refetch } = useTechnologies({ active: 'all' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
      const [formData, setFormData] = useState({
    name: '',
    category: 'Frontend',
    logo: '',
    order: 0,
    isActive: true
  });
  const [isSaving, setIsSaving] = useState(false);

  const categories = ['Frontend', 'Backend', 'Database', 'Cloud', 'Mobile', 'AI/ML', 'DevOps', 'Language', 'API', 'Testing', 'Other'];

  const handleOpenModal = (tech = null) => {
    if (tech) {
      setFormData({
        name: tech.name || '',
        category: tech.category || 'Frontend',
        logo: tech.logo || '',
        order: tech.order || 0,
        isActive: tech.isActive !== undefined ? tech.isActive : true
      });
      setEditingTech(tech);
    } else {
      setFormData({
        name: '',
        category: 'Frontend',
        logo: '',
        order: 0,
        isActive: true
      });
      setEditingTech(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTech(null);
    setFormData({
      name: '',
      category: 'Frontend',
      logo: '',
      order: 0,
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (editingTech) {
        await technologiesAPI.update(editingTech.id, formData);
        toast.success('Technology updated successfully!');
      } else {
        await technologiesAPI.create(formData);
        toast.success('Technology added successfully!');
      }
      
      await refetch();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save technology');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await technologiesAPI.delete(id);
        toast.success('Technology deleted successfully!');
        await refetch();
      } catch (error) {
        toast.error('Failed to delete technology');
      }
    }
  };

  const handleToggleActive = async (tech) => {
    try {
      await technologiesAPI.update(tech.id, { isActive: !tech.isActive });
      toast.success(`Technology ${!tech.isActive ? 'activated' : 'deactivated'}`);
      await refetch();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const groupedTechnologies = {};
  if (technologies) {
    technologies.forEach(tech => {
      if (!groupedTechnologies[tech.category]) {
        groupedTechnologies[tech.category] = [];
      }
      groupedTechnologies[tech.category].push(tech);
    });
  }

  return (
    <AdminLayout title="Technologies">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Technology Stack</h1>
            <p className="text-gray-600 mt-1">Manage technologies displayed on your website</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Technology
          </button>
        </div>

        {/* Technologies Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.keys(groupedTechnologies).length > 0 ? (
              Object.entries(groupedTechnologies).map(([category, techs]) => (
                <div key={category} className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">{category}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {techs.map((tech) => (
                      <div
                        key={tech.id}
                        className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                          !tech.isActive ? 'opacity-50 bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            {tech.logo ? (
                              <img
                                src={tech.logo}
                                alt={tech.name}
                                className="w-10 h-10"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-yellow-500 rounded-lg flex items-center justify-center text-white font-bold">
                                {tech.name.charAt(0)}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-gray-900 truncate">
                                {tech.name}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {tech.category}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-2">
                            <button
                              onClick={() => handleToggleActive(tech)}
                              className={`p-1 rounded ${
                                tech.isActive
                                  ? 'text-green-600 hover:bg-green-50'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={tech.isActive ? 'Active' : 'Inactive'}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleOpenModal(tech)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(tech.id, tech.name)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white shadow rounded-lg p-12 text-center">
                <p className="text-gray-500">No technologies found. Add your first technology to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>

              <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingTech ? 'Edit Technology' : 'Add New Technology'}
                  </h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Technology Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                        placeholder="e.g., React, Node.js"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category *
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

              <ImageInput
                label="Technology Logo"
                value={formData.logo}
                onChange={(url) => setFormData(prev => ({ ...prev, logo: url }))}
                placeholder="Select logo from Media Library"
                category="technologies"
              />                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Order
                      </label>
                      <input
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Lower numbers appear first
                      </p>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Active (visible on website)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : (editingTech ? 'Update Technology' : 'Add Technology')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TechnologiesPage;
